import { useEffect, useRef } from 'react'
import { useRealtimeOptional, type FileEvent } from '@papan/realtime/client'
import { APP_SETTINGS_FILE, isChainPointer } from '@papan/types'

const DEBOUNCE_MS = 300

/**
 * Whether a file event can change a PDApp's outstanding consent: a write to its
 * automation config (`triggers.json`, which lives in the app's `_appsystem/`
 * folder — the caller may not know that folder's id, so it is matched by name;
 * another app's write costs one redundant refetch), or a row entering or leaving
 * the app folder itself (a `.chain` grant request, the control folder).
 */
export function isConsentRelevantFileEvent(event: FileEvent, folderId: string): boolean {
  // The app folder row itself (rename / delete / restore changes what it is).
  if (event.fileId === folderId) return true
  const name = event.name ?? event.file?.name ?? ''
  // The automation config, wherever it lives (its parent is the app's control
  // folder, whose id the caller may not know; another app's write costs one
  // redundant refetch).
  if (name === 'triggers.json') return true
  // The app's Variables and secrets (0326): a secret naming a new key, or none, changes what setup asks for.
  if (name === 'env.json') return true
  // #1544: this copy's values (a secret's key, a trigger's on/off) — the effective view setup asks about.
  if (name === APP_SETTINGS_FILE) return true
  // A `.chain` entering or leaving the app folder — a grant request appearing or
  // going away. Matched by NAME as well as parent so a delete event, which omits
  // the row, still counts. Deliberately NOT "anything under the app folder": a
  // running app writes its own folder constantly and none of that can change
  // consent, so that predicate re-read the whole consent state on every write.
  const isChainRow = event.file?.fileType === 'chain' || isChainPointer(name)
  return isChainRow && (event.parentId === folderId || event.oldParentId === folderId)
}

/**
 * Calls `onChange` (debounced) whenever this PDApp's consent state may have
 * changed — so the permission gate, the pending badge and the review wizard
 * update live instead of only when the tab is opened. Events are hints: the
 * callback must refetch the authoritative state. Inert without a realtime
 * provider (share views, tests).
 */
export function useMiniappConsentSignal(folderId: string, onChange: () => void, enabled = true): void {
  const rt = useRealtimeOptional()
  // The provider's subscribe functions are stable; depending on the whole context
  // value would resubscribe on every presence/status tick.
  const onFileEvent = rt?.onFileEvent
  const onPermissionChanged = rt?.onPermissionChanged
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    if (!enabled) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const schedule = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => onChangeRef.current(), DEBOUNCE_MS)
    }
    const offFile = onFileEvent?.((event) => { if (isConsentRelevantFileEvent(event, folderId)) schedule() })
    const offPerm = onPermissionChanged?.((event) => { if (event.affectedFolderId === folderId) schedule() })
    return () => {
      if (timer) clearTimeout(timer)
      offFile?.()
      offPerm?.()
    }
  }, [folderId, enabled, onFileEvent, onPermissionChanged])
}
